import { Controller } from "@hotwired/stimulus"

// Search keys for a string, so "u" finds "Grüner" and "ue" does too, "Albarino"
// finds "Albariño", and "o" finds "Rosé". Accents are stripped; ä/ö/ü also get
// the German spelling (ae/oe/ue), and ß, æ, ø, å fold to their plain-letter forms.
function searchForms(text) {
  const trimmed = (text || "").trim().toLowerCase()
  if (trimmed === "") return []

  const stripped = trimmed
    .replace(/ß/g, "ss")
    .replace(/æ/g, "ae")
    .replace(/œ/g, "oe")
    .replace(/[øð]/g, "o")
    .replace(/ł/g, "l")
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")

  const german = trimmed
    .replace(/ß/g, "ss")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")

  return [...new Set([stripped, german])]
}

// Connects to data-controller="multiselect"
export default class extends Controller {
  static targets = ["input", "dropdown", "search", "option", "selected", "hiddenInput"]
  static values = {
    placeholder: { type: String, default: "Select options..." },
    searchPlaceholder: { type: String, default: "Search..." },
    popularOptions: Array // List of popular option values to show initially
  }

  connect() {
    this.selectedValues = new Set()
    this.initializeFromHiddenInputs()
    this.updateDisplay()
    
    // For performance with large lists, initially hide options beyond first 50
    this.optimizeInitialRender()
  }

  optimizeInitialRender() {
    // Show popular options + selected values, hide the rest
    const popularSet = new Set(this.popularOptionsValue || [])
    
    this.optionTargets.forEach(option => {
      const value = option.dataset.value
      const isPopular = popularSet.has(value)
      const isSelected = this.selectedValues.has(value)
      
      if (isPopular || isSelected) {
        option.classList.remove("hidden")
      } else {
        option.classList.add("hidden")
      }
    })
  }

  initializeFromHiddenInputs() {
    // Load any pre-selected values from hidden inputs
    this.hiddenInputTargets.forEach(input => {
      if (input.value) {
        this.selectedValues.add(input.value)
      }
    })
  }

  toggleDropdown(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const isOpen = !this.dropdownTarget.classList.contains("hidden")
    
    if (isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove("hidden")
    this.searchTarget.focus()
    
    // Close on outside click
    document.addEventListener("click", this.boundCloseOnOutside = this.closeOnOutside.bind(this))
  }

  closeDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.searchTarget.value = ""
    this.filterOptions()
    
    if (this.boundCloseOnOutside) {
      document.removeEventListener("click", this.boundCloseOnOutside)
    }
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  toggleOption(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    const label = event.currentTarget.dataset.label
    
    if (this.selectedValues.has(value)) {
      this.selectedValues.delete(value)
      this.setOptionUnselected(event.currentTarget)
    } else {
      this.selectedValues.add(value)
      this.setOptionSelected(event.currentTarget)
    }
    
    this.updateDisplay()
    this.updateHiddenInputs()
  }

// Replaces the selection programmatically, e.g. with values extracted by AI.
// Values missing from the option list are kept and shown as custom entries.
setValues(values) {
  this.selectedValues = new Set(values.filter(Boolean))
  this.updateDisplay()
  this.updateHiddenInputs()
  this.optimizeInitialRender()
}

  setOptionSelected(option) {
    option.classList.add("multiselect-option-selected")
    option.classList.remove("multiselect-option-unselected")
  }

  setOptionUnselected(option) {
    option.classList.remove("multiselect-option-selected")
    option.classList.add("multiselect-option-unselected")
  }

  removeValue(event) {
    event.preventDefault()
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    this.selectedValues.delete(value)
    
    // Update the option styling (if it exists in the predefined list)
    this.optionTargets.forEach(option => {
      if (option.dataset.value === value) {
        this.setOptionUnselected(option)
      }
    })
    
    this.updateDisplay()
    this.updateHiddenInputs()
  }

filterOptions() {
  const queries = searchForms(this.searchTarget.value)

  if (queries.length === 0) {
    // Reset to initial optimized state when search is cleared
    this.optimizeInitialRender()
    return
  }

  // Show only matching options (limited for performance)
  let visibleCount = 0
  const MAX_RESULTS = 100

  this.optionTargets.forEach(option => {
    const labels = searchForms(option.dataset.label)
    const matches = queries.some(query => labels.some(label => label.includes(query)))

    if (matches && visibleCount < MAX_RESULTS) {
      option.classList.remove("hidden")
      visibleCount++
    } else {
      option.classList.add("hidden")
    }
  })
}

  updateDisplay() {
    // Clear current selected display
    this.selectedTarget.innerHTML = ""
    
    if (this.selectedValues.size === 0) {
      const placeholder = document.createElement("span")
      placeholder.className = "text-gray-400 dark:text-surface-500"
      placeholder.textContent = this.placeholderValue
      this.selectedTarget.appendChild(placeholder)
    } else {
      this.selectedValues.forEach(value => {
        const option = this.optionTargets.find(opt => opt.dataset.value === value)
        // Show badge even for custom values not in the predefined list
        const label = option ? option.dataset.label : value
        const isCustom = !option
        const badge = this.createBadge(value, label, isCustom)
        this.selectedTarget.appendChild(badge)
      })
    }

    // Update option checkmarks
    this.optionTargets.forEach(option => {
      const checkbox = option.querySelector("svg")
      if (this.selectedValues.has(option.dataset.value)) {
        checkbox.classList.remove("invisible")
        this.setOptionSelected(option)
      } else {
        checkbox.classList.add("invisible")
        this.setOptionUnselected(option)
      }
    })
  }

  createBadge(value, label, isCustom = false) {
    const badge = document.createElement("span")
    // Custom values get a different style to indicate they're not from the predefined list
    const baseClasses = "inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium"
    const colorClasses = isCustom 
      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" 
      : "bg-primary-2-100 dark:bg-primary-2-900/30 text-primary-2-700 dark:text-primary-2-300"
    badge.className = `${baseClasses} ${colorClasses}`
    
    const customIndicator = isCustom ? '<span class="text-xs opacity-70">(custom)</span> ' : ''
    badge.innerHTML = `
      ${customIndicator}${label}
      <button type="button" 
              data-value="${value}" 
              data-action="click->multiselect#removeValue"
              class="hover:opacity-70 focus:outline-none">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `
    return badge
  }

  updateHiddenInputs() {
    // Remove all existing hidden inputs for this field
    const container = this.element.querySelector('[data-multiselect-hidden-container]')
    container.innerHTML = ""
    
    // Create new hidden inputs for each selected value
    this.selectedValues.forEach(value => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.element.dataset.multiselectName
      input.value = value
      input.setAttribute("data-multiselect-target", "hiddenInput")
      container.appendChild(input)
    })
  }

  disconnect() {
    if (this.boundCloseOnOutside) {
      document.removeEventListener("click", this.boundCloseOnOutside)
    }
  }
}

